// =============================================================================
// ProductPickerModal — choose an Unleashed product code (draggable modal)
// =============================================================================
// Opened from the Picker view's Product Code field so a picker can pick the ACTUAL
// Unleashed stock code for a device SO line. Lists the Products catalogue
// (unleashed_products) showing Code + Description — that's why it's a modal, not a
// plain <select> — with a Group filter (defaulted to the current code's group, e.g.
// "Laptop/iPad" for a device) + a search box; selecting a row returns its
// product_code. Read-only over the catalogue (editing products stays on the Products
// admin screen). Reuses the draggable ModalBackdrop (John, 1 Aug 2026).
// =============================================================================
import React from "react";
import { listProducts } from "../../data/unleashedProducts";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const cellTh = {
  textAlign: "left",
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #E0EDEB",
};
const cellTd = { padding: "6px 10px", fontSize: 13, borderBottom: "1px solid #F0F0F0" };

export function ProductPickerModal({ currentCode, onSelect, onClose }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [groupFilter, setGroupFilter] = React.useState("All");

  React.useEffect(
    function () {
      let cancelled = false;
      listProducts({ limit: 5000 }).then(function (data) {
        if (cancelled) return;
        const list = data || [];
        setRows(list);
        // Default the group filter to the CURRENT code's group (e.g. Laptop/iPad for a
        // device), so the list opens pre-narrowed without hardcoding a group name.
        const cur = list.find(function (r) {
          return r.product_code === currentCode;
        });
        if (cur?.product_group) setGroupFilter(cur.product_group);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [currentCode]
  );

  const groups = React.useMemo(
    function () {
      const set = new Set();
      for (const r of rows) if (r.product_group) set.add(r.product_group);
      return [...set].sort(function (a, b) {
        return a.localeCompare(b);
      });
    },
    [rows]
  );

  const q = search.trim().toLowerCase();
  const shown = rows.filter(function (r) {
    if (r.deactivated) return false;
    if (groupFilter !== "All" && (r.product_group || "") !== groupFilter) return false;
    if (!q) return true;
    return (
      (r.product_code || "").toLowerCase().includes(q) ||
      (r.product_description || "").toLowerCase().includes(q)
    );
  });

  const inputCls =
    "py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle="Choose product code"
      contentStyle={{ maxWidth: 720, width: "92vw", background: "#fff", borderRadius: 10 }}
    >
      <div style={{ padding: 20, maxHeight: "72vh", overflow: "auto" }}>
        <div
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
          className="mb-3"
        >
          <input
            value={search}
            onChange={function (e) {
              setSearch(e.target.value);
            }}
            placeholder="Search code or description…"
            aria-label="Search products"
            className={`${inputCls} w-[300px]`}
            style={{ fontFamily: "inherit" }}
          />
          <select
            aria-label="Filter by group"
            value={groupFilter}
            onChange={function (e) {
              setGroupFilter(e.target.value);
            }}
            className={inputCls}
            style={{ fontFamily: "inherit" }}
          >
            <option value="All">All groups</option>
            {groups.map(function (g) {
              return (
                <option key={g} value={g}>
                  {g}
                </option>
              );
            })}
          </select>
          <span style={{ fontSize: 12, color: "#607D79" }}>
            {shown.length} product{shown.length === 1 ? "" : "s"}
          </span>
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "#607D79" }}>Loading…</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cellTh}>Code</th>
                <th style={cellTh}>Description</th>
                <th style={{ ...cellTh, width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {shown.map(function (r) {
                const isCurrent = r.product_code === currentCode;
                return (
                  <tr key={r.id} style={{ background: isCurrent ? "#E8F5E9" : "transparent" }}>
                    <td style={{ ...cellTd, fontWeight: 600 }}>{r.product_code}</td>
                    <td style={cellTd}>{r.product_description || "—"}</td>
                    <td style={cellTd}>
                      <Btn
                        onClick={function () {
                          onSelect(r.product_code);
                        }}
                      >
                        {isCurrent ? "Current" : "Select"}
                      </Btn>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...cellTd, textAlign: "center", color: "#607D79" }}>
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </ModalBackdrop>
  );
}
